/**
 * PopupManager - Gestión de popups con Flowbite
 *
 * Inicializa automáticamente todos los popups con clase .popup-component
 * y maneja auto-show, localStorage, y funciones de reseteo.
 */

class PopupManager {
    constructor() {
        this.popups = new Map();
        this.init();
    }

    init() {
        // Verificar que Modal de Flowbite esté disponible
        if (typeof window.Modal === 'undefined') {
            console.error('Flowbite Modal no está disponible. Asegúrate de cargar Flowbite primero.');
            return;
        }

        // Buscar todos los popups en la página
        const popupElements = document.querySelectorAll('.popup-component');

        if (popupElements.length === 0) {
            console.log('No se encontraron popups en esta página.');
            return;
        }

        popupElements.forEach(popupElement => {
            this.initializePopup(popupElement);
        });

        console.log(`✓ ${popupElements.length} popup(s) inicializado(s)`);
    }

    initializePopup(popupElement) {
        const popupId = popupElement.id;

        if (!popupId) {
            console.warn('Popup sin ID encontrado, se omitirá:', popupElement);
            return;
        }

        // Obtener configuración del popup
        const config = {
            autoShow: popupElement.dataset.popupAutoShow === 'true',
            delay: parseInt(popupElement.dataset.popupDelay) || 2000,
            showOnce: popupElement.dataset.popupShowOnce === 'true',
            storageKey: `popup_shown_${popupId}`
        };

        // Crear instancia del modal de Flowbite
        const modalInstance = new window.Modal(popupElement, {
            placement: 'center',
            backdrop: 'dynamic',
            backdropClasses: 'bg-gray-900/70 fixed inset-0 z-40',
            closable: true,
            onHide: () => {
                if (config.showOnce) {
                    localStorage.setItem(config.storageKey, 'true');
                }
                // NUEVO: Limpiar backdrop manualmente por si acaso
                this.cleanupBackdrop();
            },
            onShow: () => {
                console.log(`Popup "${popupId}" mostrado`);
            }
        });

        // MEJORADO: Configurar botón de cierre con preventDefault
        const closeButton = popupElement.querySelector('[data-modal-hide]');
        if (closeButton) {
            closeButton.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.hidePopupSafely(popupId);
            });
        }

        // MEJORADO: Click en el backdrop para cerrar
        popupElement.addEventListener('click', (e) => {
            if (e.target === popupElement) {
                e.preventDefault();
                e.stopPropagation();
                this.hidePopupSafely(popupId);
            }
        });

        // Guardar instancia
        this.popups.set(popupId, {
            element: popupElement,
            instance: modalInstance,
            config: config
        });

        // Exponer instancia globalmente
        window[`modalInstance_${popupId}`] = modalInstance;

        // Exponer función de reset globalmente
        window[`resetPopup_${popupId}`] = () => this.resetPopup(popupId);

        // Auto-show si está configurado
        if (config.autoShow) {
            this.handleAutoShow(popupId, modalInstance, config);
        }
    }

    handleAutoShow(popupId, modalInstance, config) {
        const wasShown = config.showOnce && localStorage.getItem(config.storageKey);

        if (wasShown) {
            console.log(`ℹ Popup "${popupId}" ya fue visto. Usa: resetPopup_${popupId}()`);
            return;
        }

        console.log(`⏳ Popup "${popupId}" se mostrará en ${config.delay}ms`);

        setTimeout(() => {
            modalInstance.show();
        }, config.delay);
    }

    // NUEVO: Método seguro para ocultar popup
    hidePopupSafely(popupId) {
        const popup = this.popups.get(popupId);
        if (popup) {
            try {
                popup.instance.hide();
            } catch (error) {
                console.error(`Error al cerrar popup ${popupId}:`, error);
                // Fallback: limpiar manualmente
                this.cleanupBackdrop();
                popup.element.classList.add('hidden');
                popup.element.classList.remove('flex');
            }
        }
    }

    // NUEVO: Limpiar backdrop manualmente
    cleanupBackdrop() {
        // Buscar y eliminar cualquier backdrop huérfano
        const backdrops = document.querySelectorAll('[modal-backdrop], .modal-backdrop, div[class*="bg-gray-900"][class*="fixed"][class*="inset-0"]');
        backdrops.forEach(backdrop => {
            backdrop.remove();
        });

        // Restaurar overflow del body
        document.body.style.overflow = '';
        document.body.classList.remove('overflow-hidden');
    }

    // Métodos públicos
    showPopup(popupId) {
        const popup = this.popups.get(popupId);
        if (popup) {
            popup.instance.show();
        } else {
            console.warn(`Popup "${popupId}" no encontrado`);
        }
    }

    hidePopup(popupId) {
        this.hidePopupSafely(popupId);
    }

    togglePopup(popupId) {
        const popup = this.popups.get(popupId);
        if (popup) {
            popup.instance.toggle();
        } else {
            console.warn(`Popup "${popupId}" no encontrado`);
        }
    }

    resetPopup(popupId) {
        const popup = this.popups.get(popupId);
        if (popup) {
            localStorage.removeItem(popup.config.storageKey);
            console.log(`✓ Popup "${popupId}" reseteado. Recarga la página para verlo de nuevo.`);
        } else {
            console.warn(`Popup "${popupId}" no encontrado`);
        }
    }

    resetAllPopups() {
        this.popups.forEach((popup, popupId) => {
            localStorage.removeItem(popup.config.storageKey);
        });
        console.log('✓ Todos los popups reseteados. Recarga la página.');
    }

    getPopup(popupId) {
        return this.popups.get(popupId);
    }

    getAllPopups() {
        return Array.from(this.popups.keys());
    }

    // NUEVO: Método público para limpiar todos los backdrops
    cleanAllBackdrops() {
        this.cleanupBackdrop();
    }
}

module.exports = PopupManager;
